/**
 * Permission utilities for groups and actions.
 *
 * Group permissions use the group's user_permission level.
 * Action permissions use action.user_permission from the API, which reflects
 * the user's actual permission on the action resource directly.
 */

import { ResourcePermissionRole } from 'src/features/chat/types';
import { Action, Group } from './types';

// ============================================================================
// Group Permission Helpers
// ============================================================================

export const hasOwnerPermission = (group: Group) => group.user_permission >= ResourcePermissionRole.OWNER;
const hasContributorPermission = (group: Group) => group.user_permission >= ResourcePermissionRole.WRITE;

/**
 * Check if the user can perform write operations on meetings in a group.
 * Requires WRITE (Contributor) permission on the group.
 */
export const canEditMeeting = (group: Group): boolean => {
  return hasContributorPermission(group);
};

/**
 * Check if the user can create actions in a group.
 * Requires WRITE (Contributor) permission on the group.
 */
export const canCreateGroupAction = (group: Group): boolean => {
  return hasContributorPermission(group);
};

export const canAddGroupWikiDocument = (group: Group): boolean => {
  return hasContributorPermission(group);
};

export const canArchiveGroup = (group: Group): boolean => {
  return hasOwnerPermission(group);
};

// ============================================================================
// Action Permission Helpers (use action.user_permission from the API)
// ============================================================================

export const actionCanEdit = (action: Action): boolean => {
  if (action.status === 'resolved') return false;
  return (action.user_permission ?? 0) >= ResourcePermissionRole.WRITE;
};

export const actionCanAddUpdateOrResolve = (action: Action): boolean => {
  if (action.status === 'archived') return false;
  return (action.user_permission ?? 0) >= ResourcePermissionRole.DATA_ENTRY;
};

export const actionCanResolve = (action: Action): boolean => {
  if (action.status === 'resolved' || action.status === 'archived') return false;
  return (action.user_permission ?? 0) >= ResourcePermissionRole.DATA_ENTRY;
};

export const actionCanArchive = (action: Action): boolean => {
  if (action.status === 'resolved' || action.status === 'archived') return false;
  return (action.user_permission ?? 0) >= ResourcePermissionRole.OWNER;
};

export const actionCanUnarchive = (action: Action): boolean => {
  if (action.status !== 'archived') return false;
  return (action.user_permission ?? 0) >= ResourcePermissionRole.OWNER;
};

// Reopening mirrors resolving: whoever can resolve an action (assignees and
// higher, i.e. DATA_ENTRY) can reopen it once it is resolved.
export const actionCanUnresolve = (action: Action): boolean => {
  if (action.status !== 'resolved') return false;
  return (action.user_permission ?? 0) >= ResourcePermissionRole.DATA_ENTRY;
};
